import { Fragment } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../utils/api";
import { ordinalDate } from "../utils/date";
import { VerdictStamp } from "./Verdict";
import { cloudinaryImg } from "../utils/cloudinary";
import "./PostCard.css";

export default function PostCard({ post, variant = 1, user, onDelete, onClick }) {
  const navigate = useNavigate();
  const isPaper = variant % 2 === 0;
  const plainExcerpt = (post.subtitle || post.meta_description || "").slice(0, 120);
  const author = post.author_detail?.username;
  const coAuthors_detail = post.co_authors_detail || [];
  const category = post.category_detail?.name;
  const verdictType = post.verdict ?? post.content?.blocks?.find(b => b.type === "verdict")?.data?.type;
  const isTestPost = (post.tags_detail || []).some(t => t.slug === "test");
  const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })();
  const isScheduled = user?.is_admin && post.publish_date && post.publish_date > today;
  const date = ordinalDate(post.publish_date || post.created_at);

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this post?")) return;
    try {
      await api.delete(`/api/posts/${post.slug}/`);
      if (onDelete) onDelete(post.id);
    } catch (err) {
      console.error(err);
      alert("Failed to delete post");
    }
  };

  const handleCardClick = () => {
    if (onClick) onClick(post);
    else navigate(`/posts/${post.slug}`);
  };

  return (
    <article
      className={`post-card${isPaper ? " post-card--paper" : " post-card--dark"}`}
      onClick={handleCardClick}
    >
      {post.featured_image && (
        <img src={cloudinaryImg(post.featured_image, { width: 800 })} alt={post.title} className="post-card-image" loading="lazy" />
      )}

      {user?.is_admin && (user?.is_super_editor || post.created_by === user.id || (post.co_authors || []).includes(user.id)) && (
        <div className="post-card-actions">
          <button
            className="post-card-action-btn"
            onClick={e => { e.stopPropagation(); navigate(`/editor/${post.slug}`); }}
            title="Edit post"
          >{"// EDIT"}</button>
          <button
            className="post-card-action-btn delete"
            onClick={handleDelete}
            title="Delete post"
          >✕ DEL</button>
        </div>
      )}

      {user?.is_admin && (isTestPost || isScheduled) && (
        <div style={{ display: "flex", gap: 4, marginBottom: 8, flexWrap: "wrap" }}>
          {isTestPost && (
            <span style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 9, fontWeight: 700,
              letterSpacing: "2px", textTransform: "uppercase",
              background: "var(--orange)", color: "var(--ink)",
              padding: "3px 8px",
            }}>{'// TEST POST'}</span>
          )}
          {isScheduled && (
            <span style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 9, fontWeight: 700,
              letterSpacing: "2px", textTransform: "uppercase",
              color: "var(--orange)",
              border: "1.5px solid var(--orange)",
              padding: "3px 8px",
            }}>◷ SCHEDULED</span>
          )}
        </div>
      )}

      <div style={{ marginTop: 14, flex: 1 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
          <h3 className="post-card-title" style={{ color: isPaper ? "var(--ink)" : "var(--paper)", flex: 1 }}>
            {post.title}
          </h3>
          {verdictType ? (
            <div style={{ marginTop: 4 }}>
              <VerdictStamp type={verdictType} size="sm" rotate={-3} invert={!isPaper} />
            </div>
          ) : category ? (
            <div className="post-card-badge" style={{
              display: "inline-block",
              padding: "3px 8px",
              background: isPaper ? "var(--orange)" : "var(--ink)",
              color: isPaper ? "var(--ink)" : "var(--orange)",
              border: isPaper ? "2px solid var(--ink)" : "2px solid var(--orange)",
              fontFamily: "'Archivo Black', sans-serif",
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              transform: "rotate(-3deg)",
              boxShadow: isPaper ? "2px 2px 0 var(--ink)" : "2px 2px 0 var(--orange)",
              whiteSpace: "nowrap",
              flexShrink: 0,
              marginTop: 4,
            }}>
              {category}
            </div>
          ) : null}
        </div>
        {plainExcerpt && (
          <p className="post-card-excerpt" style={{ color: isPaper ? "var(--gray-dim)" : "var(--paper-dim)" }}>
            {plainExcerpt}{!post.subtitle && "…"}
          </p>
        )}
      </div>

      <div
        className="post-card-meta"
        style={{ borderTopColor: isPaper ? "rgba(10,10,10,0.2)" : "rgba(242,236,225,0.15)" }}
      >
        {(author || coAuthors_detail.length > 0) && (
          <span className="post-card-author" style={{ color: isPaper ? "var(--gray)" : "var(--paper-dim)" }}>
            {author && (post.author_detail?.has_author_page ? (
              <Link
                to={`/authors/${author}`}
                onClick={e => e.stopPropagation()}
                style={{ color: "inherit", textDecoration: "none", borderBottom: "1px dotted currentColor" }}
              >{author}</Link>
            ) : author)}
            {coAuthors_detail.map((ca, i) => (
              <Fragment key={ca.id}>
                {(author || i > 0) ? " & " : ""}
                {ca.has_author_page ? (
                  <Link
                    to={`/authors/${ca.username}`}
                    onClick={e => e.stopPropagation()}
                    style={{ color: "inherit", textDecoration: "none", borderBottom: "1px dotted currentColor" }}
                  >{ca.username}</Link>
                ) : ca.username}
              </Fragment>
            ))}
          </span>
        )}
        <span className="post-card-date" style={{ color: isPaper ? "var(--gray)" : "var(--gray)" }}>
          {date}
        </span>
      </div>
    </article>
  );
}
